<template>
  <div class="container-fluid py-4">
    <div class="card">
      <div class="card-header pb-0">
        <h6>인증서 이미지 관리</h6>
      </div>
      <div class="card-body">
        <!-- 필터 영역 -->
        <div class="filter-section mb-4">
          <a-row :gutter="16">
            <a-col :span="6">
              <label class="form-label">진료과목</label>
              <a-select
                v-model:value="selectedHospitalType"
                placeholder="진료과목 선택"
                style="width: 100%"
                @change="handleHospitalTypeChange"
              >
                <a-select-option v-for="type in hospitalTypes" :key="type.code" :value="type.code">
                  {{ type.name }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :span="6">
              <label class="form-label">제품 유형</label>
              <a-select
                v-model:value="selectedSurgeryType"
                placeholder="제품 유형 선택"
                style="width: 100%"
                :disabled="!selectedHospitalType"
                @change="handleSurgeryTypeChange"
              >
                <a-select-option v-for="type in filteredSurgeryTypes" :key="type.code" :value="type.code">
                  {{ type.name }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :span="6">
              <label class="form-label">제조사</label>
              <a-select
                v-model:value="selectedManufacturer"
                placeholder="제조사 선택"
                style="width: 100%"
                :disabled="!selectedSurgeryType"
                :loading="manufacturerLoading"
                @change="handleManufacturerChange"
              >
                <a-select-option v-for="manu in manufacturers" :key="manu._id" :value="manu._id">
                  {{ manu.name }}
                </a-select-option>
              </a-select>
            </a-col>
          </a-row>
        </div>

        <!-- 이미지 관리 영역 -->
        <div v-if="selectedManufacturerData" class="upload-section">
          <div class="section-header mb-3">
            <h6>{{ currentSelectionText }} 인증서 이미지</h6>
            <a-tag :color="selectedManufacturerData.isCertified ? 'green' : 'default'">
              {{ selectedManufacturerData.isCertified ? '인증됨' : '미인증' }}
            </a-tag>
          </div>

          <!-- 이미지 카드들 -->
          <a-row :gutter="[24, 24]">
            <!-- 아이콘 이미지 -->
            <a-col :span="8">
              <div class="image-card-wrapper">
                <div class="image-label">
                  아이콘 (iconUrl)
                  <a-tag v-if="pendingFiles.iconUrl" color="orange" class="ms-2">변경됨</a-tag>
                </div>
                <div class="image-card">
                  <div v-if="getDisplayUrl('iconUrl')" class="image-wrapper">
                    <img :src="getDisplayUrl('iconUrl')" alt="아이콘" @click="handlePreview(getDisplayUrl('iconUrl'))" />
                    <div class="image-actions">
                      <a-tooltip title="다운로드" v-if="selectedManufacturerData.iconUrl && !pendingFiles.iconUrl">
                        <a-button type="text" size="small" @click.stop="handleDownload(selectedManufacturerData.iconUrl, 'icon')">
                          <template #icon><download-outlined /></template>
                        </a-button>
                      </a-tooltip>
                    </div>
                  </div>
                  <div v-else class="empty-image">
                    <span>이미지 없음</span>
                  </div>
                  <a-upload
                    :show-upload-list="false"
                    :before-upload="handleBeforeUpload"
                    :custom-request="(options) => handleUpload(options, 'iconUrl')"
                    :accept="acceptImageTypes"
                  >
                    <a-button type="dashed" block>
                      <upload-outlined /> {{ getDisplayUrl('iconUrl') ? '변경' : '업로드' }}
                    </a-button>
                  </a-upload>
                </div>
              </div>
            </a-col>

            <!-- 메인 이미지 -->
            <a-col :span="8">
              <div class="image-card-wrapper">
                <div class="image-label">
                  메인 이미지 (imgUrl)
                  <a-tag v-if="pendingFiles.imgUrl" color="orange" class="ms-2">변경됨</a-tag>
                </div>
                <div class="image-card">
                  <div v-if="getDisplayUrl('imgUrl')" class="image-wrapper">
                    <img :src="getDisplayUrl('imgUrl')" alt="메인 이미지" @click="handlePreview(getDisplayUrl('imgUrl'))" />
                    <div class="image-actions">
                      <a-tooltip title="다운로드" v-if="selectedManufacturerData.imgUrl && !pendingFiles.imgUrl">
                        <a-button type="text" size="small" @click.stop="handleDownload(selectedManufacturerData.imgUrl, 'main')">
                          <template #icon><download-outlined /></template>
                        </a-button>
                      </a-tooltip>
                    </div>
                  </div>
                  <div v-else class="empty-image">
                    <span>이미지 없음</span>
                  </div>
                  <a-upload
                    :show-upload-list="false"
                    :before-upload="handleBeforeUpload"
                    :custom-request="(options) => handleUpload(options, 'imgUrl')"
                    :accept="acceptImageTypes"
                  >
                    <a-button type="dashed" block>
                      <upload-outlined /> {{ getDisplayUrl('imgUrl') ? '변경' : '업로드' }}
                    </a-button>
                  </a-upload>
                </div>
              </div>
            </a-col>

            <!-- 서브 이미지 -->
            <a-col :span="8">
              <div class="image-card-wrapper">
                <div class="image-label">
                  서브 이미지 (imgUrl2)
                  <a-tag v-if="pendingFiles.imgUrl2" color="orange" class="ms-2">변경됨</a-tag>
                </div>
                <div class="image-card">
                  <div v-if="getDisplayUrl('imgUrl2')" class="image-wrapper">
                    <img :src="getDisplayUrl('imgUrl2')" alt="서브 이미지" @click="handlePreview(getDisplayUrl('imgUrl2'))" />
                    <div class="image-actions">
                      <a-tooltip title="다운로드" v-if="selectedManufacturerData.imgUrl2 && !pendingFiles.imgUrl2">
                        <a-button type="text" size="small" @click.stop="handleDownload(selectedManufacturerData.imgUrl2, 'sub')">
                          <template #icon><download-outlined /></template>
                        </a-button>
                      </a-tooltip>
                    </div>
                  </div>
                  <div v-else class="empty-image">
                    <span>이미지 없음</span>
                  </div>
                  <a-upload
                    :show-upload-list="false"
                    :before-upload="handleBeforeUpload"
                    :custom-request="(options) => handleUpload(options, 'imgUrl2')"
                    :accept="acceptImageTypes"
                  >
                    <a-button type="dashed" block>
                      <upload-outlined /> {{ getDisplayUrl('imgUrl2') ? '변경' : '업로드' }}
                    </a-button>
                  </a-upload>
                </div>
              </div>
            </a-col>
          </a-row>

          <!-- 제조사 정보 -->
          <div class="info-section mt-4">
            <a-descriptions title="제조사 정보" :column="2" bordered size="small">
              <a-descriptions-item label="제조사명">{{ selectedManufacturerData.name }}</a-descriptions-item>
              <a-descriptions-item label="NFT 타이틀">{{ selectedManufacturerData.nftTitle }}</a-descriptions-item>
              <a-descriptions-item label="색상 코드">
                <div class="color-preview">
                  <span class="color-box" :style="{ backgroundColor: selectedManufacturerData.colorCode }"></span>
                  {{ selectedManufacturerData.colorCode }}
                </div>
              </a-descriptions-item>
              <a-descriptions-item label="표시 순서">{{ selectedManufacturerData.displayOrder }}</a-descriptions-item>
              <a-descriptions-item label="설명" :span="2">{{ selectedManufacturerData.description || '-' }}</a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- 저장 버튼 -->
          <div class="save-section mt-4">
            <a-button
              type="primary"
              size="large"
              class="yellow-button"
              :loading="saveLoading"
              @click="handleSave"
            >
              저장
            </a-button>
          </div>
        </div>

        <!-- 선택 안내 -->
        <div v-else class="empty-state">
          <a-empty description="진료과목, 제품 유형, 제조사를 선택해주세요" />
        </div>
      </div>
    </div>

    <!-- 이미지 미리보기 모달 -->
    <a-modal
      :visible="previewVisible"
      :footer="null"
      :width="800"
      @cancel="previewVisible = false"
    >
      <img :src="previewImage" style="width: 100%" />
    </a-modal>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { message } from 'ant-design-vue';
import { UploadOutlined, DownloadOutlined } from '@ant-design/icons-vue';
import { getManufacturersBySurgery, updateManufacturerImage, uploadCertificateImage } from './api';

export default {
  name: 'CertificateImageManagement',
  components: {
    UploadOutlined,
    DownloadOutlined,
  },
  setup() {
    const selectedHospitalType = ref(null);
    const selectedSurgeryType = ref(null);
    const selectedManufacturer = ref(null);
    const manufacturers = ref([]);
    const manufacturerLoading = ref(false);
    const saveLoading = ref(false);
    const previewVisible = ref(false);
    const previewImage = ref('');
    const acceptImageTypes = 'image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml';

    // 업로드 대기 중인 파일들 (저장 버튼 클릭 전까지 보관)
    const pendingFiles = ref({
      iconUrl: null,
      imgUrl: null,
      imgUrl2: null,
    });

    // 로컬 미리보기 URL
    const previewUrls = ref({
      iconUrl: null,
      imgUrl: null,
      imgUrl2: null,
    });

    // 진료과목 (MedicalDept)
    const hospitalTypes = [
      { code: 'DENT', name: '치과' },
      { code: 'OPH', name: '안과' },
      { code: 'PS', name: '성형외과' },
      { code: 'DERM', name: '피부과' },
      { code: 'OS', name: '정형외과' },
      { code: 'VET_COMMON', name: '수의과' },
      { code: 'COMMON', name: '공통' },
    ];

    // 제품 유형 (ProductType) - 진료과목별
    const surgeryTypesByHospital = {
      DENT: [
        { code: 'IMPLANT', name: '임플란트' },
        { code: 'ABUTMENT', name: '보철' },
        { code: 'ZIRCONIA', name: '지르코니아' },
      ],
      OPH: [],
      PS: [],
      DERM: [],
      OS: [],
      VET_COMMON: [],
      COMMON: [],
    };

    const filteredSurgeryTypes = computed(() => {
      if (!selectedHospitalType.value) return [];
      return surgeryTypesByHospital[selectedHospitalType.value] || [];
    });

    const selectedManufacturerData = computed(() => {
      if (!selectedManufacturer.value) return null;
      return manufacturers.value.find(m => m._id === selectedManufacturer.value);
    });

    const currentSelectionText = computed(() => {
      const hospital = hospitalTypes.find(h => h.code === selectedHospitalType.value);
      const surgery = filteredSurgeryTypes.value.find(s => s.code === selectedSurgeryType.value);
      const manu = selectedManufacturerData.value;
      return `${hospital?.name || ''} > ${surgery?.name || ''} > ${manu?.name || ''}`;
    });

    const handleHospitalTypeChange = () => {
      selectedSurgeryType.value = null;
      selectedManufacturer.value = null;
      manufacturers.value = [];
    };

    const handleSurgeryTypeChange = async () => {
      selectedManufacturer.value = null;

      if (!selectedHospitalType.value || !selectedSurgeryType.value) return;

      try {
        manufacturerLoading.value = true;
        const response = await getManufacturersBySurgery(selectedHospitalType.value, selectedSurgeryType.value);
        manufacturers.value = response || [];
      } catch (error) {
        console.error('제조사 목록 조회 실패:', error);
        message.error('제조사 목록을 불러오는데 실패했습니다.');
        manufacturers.value = [];
      } finally {
        manufacturerLoading.value = false;
      }
    };

    const handleManufacturerChange = () => {
      // 제조사 변경 시 대기 중인 파일 초기화
      resetPendingFiles();
    };

    // 대기 중인 파일 초기화
    const resetPendingFiles = () => {
      // 기존 미리보기 URL 해제
      Object.values(previewUrls.value).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
      pendingFiles.value = { iconUrl: null, imgUrl: null, imgUrl2: null };
      previewUrls.value = { iconUrl: null, imgUrl: null, imgUrl2: null };
    };

    // 이미지 표시 URL (미리보기 또는 기존 URL)
    const getDisplayUrl = (imageField) => {
      return previewUrls.value[imageField] || selectedManufacturerData.value?.[imageField];
    };

    // 변경된 파일이 있는지 확인
    const hasChanges = computed(() => {
      return Object.values(pendingFiles.value).some(file => file !== null);
    });

    const handleBeforeUpload = (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.warning('이미지 파일만 업로드 가능합니다.');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.warning('이미지 크기는 5MB 이하여야 합니다.');
        return false;
      }
      return true;
    };

    // 파일 선택 시 로컬에 저장 (업로드는 저장 버튼 클릭 시)
    const handleUpload = ({ file, onSuccess }, imageField) => {
      // 기존 미리보기 URL 해제
      if (previewUrls.value[imageField]) {
        URL.revokeObjectURL(previewUrls.value[imageField]);
      }

      // 파일 저장 및 미리보기 URL 생성
      pendingFiles.value[imageField] = file;
      previewUrls.value[imageField] = URL.createObjectURL(file);

      console.log(`${imageField} 파일 선택됨:`, file.name);
      message.info(`${file.name} 파일이 선택되었습니다. 저장 버튼을 눌러 업로드하세요.`);
      onSuccess();
    };

    // 저장 버튼 클릭 시 업로드 및 데이터 저장
    const handleSave = async () => {
      if (!hasChanges.value) {
        message.warning('변경된 이미지가 없습니다.');
        return;
      }

      try {
        saveLoading.value = true;
        const imageUrls = {};

        // 대기 중인 파일들 업로드
        for (const [imageField, file] of Object.entries(pendingFiles.value)) {
          if (file) {
            console.log(`${imageField} 업로드 중...`);
            const uploadResponse = await uploadCertificateImage(
              file,
              imageField,
              selectedHospitalType.value
            );

            // 응답에서 URL 추출
            if (uploadResponse.success && uploadResponse.data?.results?.length > 0) {
              imageUrls[imageField] = uploadResponse.data.results[0].url;
              console.log(`${imageField} 업로드 완료:`, imageUrls[imageField]);
            } else {
              throw new Error(`${imageField} 업로드 실패`);
            }
          }
        }

        // 제조사 정보 업데이트
        if (Object.keys(imageUrls).length > 0) {
          await updateManufacturerImage(selectedManufacturer.value, imageUrls);

          // 로컬 데이터 업데이트
          const manuIndex = manufacturers.value.findIndex(m => m._id === selectedManufacturer.value);
          if (manuIndex !== -1) {
            Object.assign(manufacturers.value[manuIndex], imageUrls);
          }
        }

        // 대기 파일 초기화
        resetPendingFiles();
        message.success('이미지가 저장되었습니다.');
      } catch (error) {
        console.error('저장 실패:', error);
        message.error(error.message || '저장에 실패했습니다.');
      } finally {
        saveLoading.value = false;
      }
    };

    const handlePreview = (url) => {
      previewImage.value = url;
      previewVisible.value = true;
    };

    const handleDownload = (url, name) => {
      if (!url) {
        message.error('다운로드할 이미지가 없습니다.');
        return;
      }

      const fileName = `${selectedManufacturerData.value?.name || 'image'}_${name}.png`;

      // 외부 URL인 경우 fetch로 다운로드
      if (url.startsWith('http')) {
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          })
          .catch(error => {
            console.error('다운로드 실패:', error);
            message.error('이미지 다운로드에 실패했습니다.');
          });
      } else {
        // blob URL인 경우 직접 다운로드
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    };

    return {
      selectedHospitalType,
      selectedSurgeryType,
      selectedManufacturer,
      manufacturers,
      manufacturerLoading,
      saveLoading,
      previewVisible,
      previewImage,
      acceptImageTypes,
      hospitalTypes,
      filteredSurgeryTypes,
      selectedManufacturerData,
      currentSelectionText,
      pendingFiles,
      previewUrls,
      hasChanges,
      handleHospitalTypeChange,
      handleSurgeryTypeChange,
      handleManufacturerChange,
      handleBeforeUpload,
      handleUpload,
      handleSave,
      handlePreview,
      handleDownload,
      getDisplayUrl,
    };
  },
};
</script>

<style scoped>
.filter-section {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #344767;
}

.upload-section {
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h6 {
  margin: 0;
}

.image-card-wrapper {
  text-align: center;
}

.image-label {
  font-weight: 500;
  margin-bottom: 12px;
  color: #344767;
}

.image-card {
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  padding: 12px;
}

.image-wrapper {
  position: relative;
  margin-bottom: 12px;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 8px;
}

.image-wrapper img {
  width: 100%;
  height: 180px;
  object-fit: contain;
  cursor: pointer;
}

.image-actions {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 4px;
  padding: 2px;
}

.empty-image {
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 4px;
  margin-bottom: 12px;
  color: #999;
}

.info-section {
  border-top: 1px solid #e8e8e8;
  padding-top: 20px;
}

.color-preview {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
}

.empty-state {
  padding: 60px 0;
  text-align: center;
}

.save-section {
  display: flex;
  justify-content: flex-end;
  padding-top: 20px;
  border-top: 1px solid #e8e8e8;
}

.yellow-button {
  background-color: #f5b100 !important;
  border-color: #f5b100 !important;
  color: #fff !important;
}

.yellow-button:hover {
  background-color: #d9a000 !important;
  border-color: #d9a000 !important;
}
</style>
